import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  MenuItem,
  CircularProgress,
  Typography,
  Box,
  Divider,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { useState, useEffect } from "react";
import { OtherProduct, OtherProductVariant } from "../interfaces/OtherProduct";
import { storage } from "../firebase/config";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useCollection } from "../hooks/useCollection";
import { useParams } from "react-router-dom";
import { InventoryItem } from "../interfaces/Inventory";


interface Props {
  open: boolean;
  onClose: () => void;
  initial: OtherProduct;
  categories: { id: string; name: string }[];
  onSubmit: (values: OtherProduct) => Promise<void>;
}

export default function OtherProductDialog({
  open,
  onClose,
  initial,
  categories,
  onSubmit,
}: Props) {
  const [values, setValues] = useState<OtherProduct>(initial);
  const [uploading, setUploading] = useState(false);
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const { data: inventory = [] } = useCollection<InventoryItem>(restaurantId!, "inventory");

  useEffect(() => {
    setValues(initial);
  }, [initial]);

  const handleChange = <K extends keyof OtherProduct>(
    key: K,
    value: OtherProduct[K]
  ) => {
    setValues((p) => ({ ...p, [key]: value }));
  };

  // 🔥 SUBIR IMAGEN A FIREBASE
  const uploadImage = async (file: File) => {
    try {
      setUploading(true);

      const storageRef = ref(
        storage,
        `otherProducts/${Date.now()}_${file.name}`
      );

      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      handleChange("image", url);
    } finally {
      setUploading(false);
    }
  };

  const handleAddVariant = () => {
    const newVariant: OtherProductVariant = {
      id: `var_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      name: "",
      price: values.price || 0,
    };
    setValues((p) => ({
      ...p,
      variants: [...(p.variants || []), newVariant],
    }));
  };

  const handleUpdateVariant = (
    index: number,
    field: keyof OtherProductVariant,
    val: any
  ) => {
    setValues((p) => {
      const list = [...(p.variants || [])];
      list[index] = { ...list[index], [field]: val };
      return { ...p, variants: list };
    });
  };

  const handleRemoveVariant = (index: number) => {
    setValues((p) => {
      const list = [...(p.variants || [])];
      list.splice(index, 1);
      return { ...p, variants: list };
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
      <DialogTitle sx={{ fontWeight: 800, fontSize: "1.25rem", pb: 2, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
        {initial.id ? "📝 Editar Producto" : "📦 Nuevo Producto"}
      </DialogTitle>

      <DialogContent sx={{ p: 4 }}>
        <Stack spacing={4} mt={1}>
          
          {/* SECCIÓN: INFORMACIÓN GENERAL */}
          <Box>
            <Typography variant="overline" color="primary" fontWeight={800} sx={{ letterSpacing: 1.2, display: "block", mb: 2 }}>
              Información General
            </Typography>
            <Stack spacing={2.5}>
              <TextField
                label="Nombre del Producto"
                value={values.name}
                onChange={(e) => handleChange("name", e.target.value)}
                fullWidth
                variant="outlined"
              />

              <TextField
                select
                label="Categoría"
                value={values.category || ""}
                onChange={(e) => handleChange("category", e.target.value)}
                fullWidth
              >
                {(() => {
                  const options = [...categories];
                  if (values.category && !options.some((c) => c.name === values.category)) {
                    options.unshift({ id: `cat_${values.category}`, name: values.category });
                  }
                  return options.map((c) => (
                    <MenuItem key={c.id} value={c.name}>
                      {c.name}
                    </MenuItem>
                  ));
                })()}
              </TextField>

              <TextField
                label="Descripción"
                value={values.description}
                onChange={(e) => handleChange("description", e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            </Stack>
          </Box>

          <Divider />

          {/* SECCIÓN: PRECIO E INVENTARIO */}
          <Box>
            <Typography variant="overline" color="primary" fontWeight={800} sx={{ letterSpacing: 1.2, display: "block", mb: 2 }}>
              Precio e Inventario
            </Typography>
            <Stack spacing={2.5}>
              <TextField
                label="Precio Base / Por Defecto ($)"
                type="text"
                value={values.price ? values.price.toLocaleString("es-CO") : ""}
                onChange={(e) => {
                  const rawStr = e.target.value.replace(/\D/g, "");
                  const val = rawStr ? Number(rawStr) : 0;
                  handleChange("price", val);
                }}
                fullWidth
                helperText="Si agregas variaciones, se usará el precio de cada variación seleccionada."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography fontWeight={700} color="text.secondary">$</Typography>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                select
                label="Vincular a Insumo (Opcional)"
                value={values.inventoryItemId || ""}
                onChange={(e) => handleChange("inventoryItemId", e.target.value)}
                fullWidth
                helperText="Si lo vinculas, cada venta descontará 1 unidad del inventario."
              >
                <MenuItem value="">-- Ninguno --</MenuItem>
                {inventory.map((item) => (
                  <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                ))}
              </TextField>
            </Stack>
          </Box>

          <Divider />

          {/* SECCIÓN: VARIACIONES */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Typography variant="overline" color="primary" fontWeight={800} sx={{ letterSpacing: 1.2 }}>
                Variaciones / Sabores (Opcional)
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddVariant}
                variant="outlined"
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
              >
                Agregar Variación
              </Button>
            </Stack>

            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
              Ejemplo: Pollo, Carne, Mixta. El valor adicional de cada variación se <strong>sumará al precio base</strong> (${values.price ? values.price.toLocaleString("es-CO") : 0}).
            </Typography>

            {(!values.variants || values.variants.length === 0) ? (
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: "#f8fafc", textAlign: "center", border: "1px dashed #cbd5e1" }}>
                <Typography variant="caption" color="text.secondary">
                  Este producto no tiene variaciones. Se venderá únicamente con el precio base.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {values.variants.map((v, idx) => (
                  <Stack key={v.id || idx} direction="row" spacing={1.5} alignItems="center">
                    <TextField
                      label={`Nombre variación (ej: Pollo)`}
                      value={v.name}
                      onChange={(e) => handleUpdateVariant(idx, "name", e.target.value)}
                      size="small"
                      sx={{ flex: 2 }}
                    />
                    <TextField
                      label="Precio Adicional ($)"
                      type="text"
                      value={v.price ? v.price.toLocaleString("es-CO") : ""}
                      onChange={(e) => {
                        const rawStr = e.target.value.replace(/\D/g, "");
                        const val = rawStr ? Number(rawStr) : 0;
                        handleUpdateVariant(idx, "price", val);
                      }}
                      size="small"
                      sx={{ flex: 1 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography variant="caption" fontWeight={700} color="text.secondary">$</Typography>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => handleRemoveVariant(idx)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>


          <Divider />

          {/* SECCIÓN: IMAGEN */}
          <Box>
            <Typography variant="overline" color="primary" fontWeight={800} sx={{ letterSpacing: 1.2, display: "block", mb: 2 }}>
              Fotografía
            </Typography>
            <Stack direction="row" spacing={3} alignItems="center">
              
              {values.image ? (
                <Box sx={{ position: "relative" }}>
                   <img
                    src={values.image}
                    alt="preview"
                    style={{
                      width: 100,
                      height: 100,
                      objectFit: "cover",
                      borderRadius: 12,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                    }}
                  />
                </Box>
              ) : (
                <Box sx={{ width: 100, height: 100, borderRadius: 3, bgcolor: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", border: "2px dashed #cbd5e1" }}>
                  <Typography variant="caption" color="text.secondary">Sin foto</Typography>
                </Box>
              )}

              <Stack spacing={1} flex={1}>
                 <Button 
                    component="label" 
                    variant="outlined" 
                    size="large"
                    startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                    disabled={uploading}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                 >
                   {uploading ? "Subiendo..." : "Subir Imagen"}
                   <input
                     hidden
                     type="file"
                     accept="image/*"
                     onChange={(e) => e.target.files && uploadImage(e.target.files[0])}
                   />
                 </Button>
                 <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                    Formatos: JPG, PNG. Diseño cuadrado (500x500px).
                 </Typography>
              </Stack>
            </Stack>
          </Box>

        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1, bgcolor: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
        <Button onClick={onClose} sx={{ color: "text.secondary", fontWeight: 700 }}>Cancelar</Button>
        <Button 
          variant="contained" 
          size="large"
          onClick={() => onSubmit(values)}
          sx={{ borderRadius: 2, px: 4, fontWeight: 800, boxShadow: "0 4px 14px rgba(0,0,0,0.15)" }}
        >
          Guardar Producto
        </Button>
      </DialogActions>
    </Dialog>
  );
}
